<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import {
  ActionTriggerMode,
  PlaybookTriggerType,
  type FindingThresholdTriggerInDb,
  type PlaybookTriggerCreateRequest,
  type PlaybookTriggerUpdate,
} from '@/api';
import { PlaybooksService } from '@/services/playbooks/playbooksService';
import { extractErrorDetail } from '@/utils/errorHandler';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaInputNumber from '@/components/design/base/inputs/GaiaInputNumber.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

type TriggerModalMode = 'create' | 'edit';
type Severity = 'low' | 'medium' | 'high';

type TriggerFormData = {
  mode: ActionTriggerMode;
  min_severity: Severity;
  min_count: number;
};

const props = withDefaults(
  defineProps<{
    show: boolean;
    mode?: TriggerModalMode;
    evaluationUid?: string | null;
    trigger?: FindingThresholdTriggerInDb | null;
  }>(),
  {
    mode: 'create',
    evaluationUid: null,
    trigger: null,
  }
);

const emit = defineEmits<{
  (event: 'update:show', value: boolean): void;
  (event: 'saved'): void;
}>();

const message = useGaiaToast();
const saving = defineModel<boolean>('saving', { default: false });

const formData = reactive<TriggerFormData>(getEmptyFormData());

const modeOptions = [
  { label: 'At least N findings', value: ActionTriggerMode.AT_LEAST },
  { label: 'Fewer than N findings', value: ActionTriggerMode.LESS_THAN },
  { label: 'No findings', value: ActionTriggerMode.NONE_FOUND },
  { label: 'Always', value: ActionTriggerMode.ALWAYS },
];

const severityOptions = [
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
];

const modalTitle = computed(() =>
  props.mode === 'create' ? 'Add finding trigger' : 'Edit finding trigger'
);
const primaryLabel = computed(() => (props.mode === 'create' ? 'Create' : 'Save'));
const showThresholdFields = computed(
  () =>
    formData.mode === ActionTriggerMode.AT_LEAST || formData.mode === ActionTriggerMode.LESS_THAN
);

watch(
  () => [props.show, props.trigger, props.mode] as const,
  () => {
    if (!props.show) return;
    resetForm();
  },
  { immediate: true }
);

function getEmptyFormData(): TriggerFormData {
  return {
    mode: ActionTriggerMode.AT_LEAST,
    min_severity: 'medium',
    min_count: 1,
  };
}

function resetForm(): void {
  if (props.trigger) {
    formData.mode = props.trigger.mode;
    formData.min_severity = props.trigger.min_severity;
    formData.min_count = props.trigger.min_count;
  } else {
    Object.assign(formData, getEmptyFormData());
  }
}

function close(): void {
  emit('update:show', false);
}

function createPayload(): PlaybookTriggerCreateRequest {
  const base: PlaybookTriggerCreateRequest = {
    trigger_type: PlaybookTriggerType.FINDING_THRESHOLD,
    mode: formData.mode,
  };
  if (showThresholdFields.value) {
    base.min_severity = formData.min_severity;
    base.min_count = formData.min_count;
  }
  return base;
}

function updatePayload(): PlaybookTriggerUpdate {
  const base: PlaybookTriggerUpdate = { mode: formData.mode };
  if (showThresholdFields.value) {
    base.min_severity = formData.min_severity;
    base.min_count = formData.min_count;
  }
  return base;
}

async function save(): Promise<void> {
  saving.value = true;
  try {
    if (props.mode === 'create') {
      if (!props.evaluationUid) {
        message.error('Save the uncommons check before adding a trigger.');
        return;
      }
      await PlaybooksService.createTrigger(props.evaluationUid, createPayload());
      message.success('Finding trigger created.');
    } else {
      if (!props.trigger) return;
      await PlaybooksService.updateTrigger(props.trigger.uid, updatePayload());
      message.success('Finding trigger updated.');
    }
    emit('saved');
    close();
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to save finding trigger.'));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <n-modal
    :show="show"
    preset="card"
    :title="modalTitle"
    style="width: min(560px, calc(100vw - 32px))"
    :mask-closable="false"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <n-form :model="formData" label-placement="top">
      <n-form-item label="Fire when" required>
        <gaia-select v-model:value="formData.mode" :options="modeOptions" />
      </n-form-item>

      <template v-if="showThresholdFields">
        <n-form-item label="Minimum severity" required>
          <gaia-select v-model:value="formData.min_severity" :options="severityOptions" />
        </n-form-item>
        <n-form-item label="Minimum count" required>
          <gaia-input-number v-model:value="formData.min_count" :min="1" style="width: 100%" />
        </n-form-item>
      </template>
    </n-form>

    <template #footer>
      <n-space justify="end">
        <n-button @click="close">Cancel</n-button>
        <n-button type="primary" :loading="saving" @click="save">
          {{ primaryLabel }}
        </n-button>
      </n-space>
    </template>
  </n-modal>
</template>
